import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { generatePath, Link } from 'react-router-dom';
import { Cell } from 'react-table';
import { Modal as BootstrapModal, Button } from 'react-bootstrap';
import _ from 'lodash';
import dayjs from 'dayjs';
import {
  Loading,
  formatNumber,
  onDateSort,
  Modal,
  EmptyBlockContent,
  ContactPhone,
  useTableColumnWidths,
} from '@tradingblock/components';
import {
  CashieringRoute,
  CashieringView,
  Transfer,
  ScheduledTransfer,
  ScheduledTransferState,
  TransferState,
} from '@tradingblock/types';
import { getTransferStatusText, getTransferMethod } from '../../utilities/cashiering';
import { BlockTable } from '../shared/BlockTable';
import { cashieringSelectors, accountIdSelector, useStateSelector, account } from '../../data/global/dataSelectors';
import { useDispatcher } from '../../data/global/hooks';
import { CashieringEntityStatus } from '../../components/cashiering/CashieringEntityStatus';
import { account as accountSelectors } from '../../data/global/selectors/accountSelectors';
import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';
import { scheduledTransfers } from '../../data/global/selectors/cashieringSelectors';
import { useCashieringAccountsList } from '../../pages/views/cashiering/components/AccountsList';
import { Formik } from 'formik';
import { useCashieringDataContext } from '../../pages/views/cashiering/data/useCashieringData';

export const AccountTransferContent: React.FunctionComponent<{}> = () => {
  const { dispatcher } = useDispatcher();

  const accountId = useSelector(accountIdSelector);
  const isIRA = useStateSelector(accountSelectors.accountIsIRA);
  const userName = useStateSelector(s => s.auth.userName);
  // even though option to add a cashiering block should be hidden, also hide action btns as additional precaution
  const accountCanAccessCashiering = useSelector(account.accountCanAccessCashiering);
  const transfers = useSelector(cashieringSelectors.transfers);
  const newTransferId = useSelector(cashieringSelectors.newTransferId);
  const isLoadingTransfers = useStateSelector(s => cashieringSelectors.dataIsLoading(s, 'transfers'));
  const achRelationships = useSelector(cashieringSelectors.achRelationships);
  const isLoadingAchRelationships = useStateSelector(s => cashieringSelectors.dataIsLoading(s, 'achRelationships'));
  const transferInstructions = useSelector(cashieringSelectors.transferInstructions);
  const isLoadingTransferInstructions = useStateSelector(s =>
    cashieringSelectors.dataIsLoading(s, 'transferInstructions')
  );
  const originalAccountId = useStateSelector(
    s => s.account && s.account.selectedLinkedAccount && s.account.selectedLinkedAccount.originalAccount.accountId
  );
  const accountStatus = useStateSelector(s => s.account.accountDetails && s.account.accountDetails.accountStatus);
  const targetedAccountStatuses =
    accountStatus === 'Rejected' || accountStatus === 'Closed' || accountStatus === 'Closed Never Funded';
  const rejectMsg =
    'Please note that the account is not eligible to make cashiering requests at the moment. Please contact the support team for more information.';
  // track if the accountId changes (e.g. when switching accounts)
  const [currentAccountId, setCurrentAccountId] = useState<number>();
  const [fetchData, setFetchData] = useState(false);
  const [editModal, setEditModal] = useState<{ open: boolean; transfer?: ScheduledTransfer }>({ open: false });
  const [editNickname, setEditNickname] = useState('');
  const [editFormValues, setEditFormValues] = useState<any>({});
  const [AccountsList, , onValidate] = useCashieringAccountsList();
  const [TransferAccountsList] = useCashieringAccountsList();
  const [hasFetchedScheduledTransfers, setHasFetchedScheduledTransfers] = useState(false);

  useEffect(() => {
    if (accountId !== currentAccountId) {
      setCurrentAccountId(accountId);
      setFetchData(true);
      setHasFetchedScheduledTransfers(false);
    }
  }, [accountId, currentAccountId, dispatcher.cashiering]);

  const [transferIdToDelete, setTransferIdToDelete] = useState<number>();

  useEffect(() => {
    if (originalAccountId && originalAccountId !== accountId && fetchData) {
      if (!isLoadingTransfers) {
        dispatcher.cashiering.transfers.all.request();
      }
      if (!isLoadingAchRelationships) {
        dispatcher.cashiering.achRelationships.all.request(accountId);
      }
      if (!isLoadingTransferInstructions) {
        dispatcher.cashiering.transferInstructions.all.request(accountId);
      }
      setFetchData(false);
    }
  }, [
    accountId,
    originalAccountId,
    isLoadingTransfers,
    isLoadingAchRelationships,
    isLoadingTransferInstructions,
    dispatcher.cashiering,
    fetchData,
  ]);

  useEffect(() => {
    // fetch transfers shown in block content (refetch until new transfer is returned in list)
    if (
      accountId &&
      !isLoadingTransfers &&
      ((!newTransferId && !transfers) || (newTransferId && !_.some(transfers, t => t.id === newTransferId)))
    ) {
      dispatcher.cashiering.transfers.all.request();
    }
  }, [accountId, transfers, newTransferId, isLoadingTransfers, dispatcher.cashiering]);

  useEffect(() => {
    // need related ach relationships for display
    if (accountId && !isLoadingAchRelationships && !achRelationships) {
      dispatcher.cashiering.achRelationships.all.request(accountId);
    }
  }, [accountId, achRelationships, isLoadingAchRelationships, dispatcher.cashiering]);

  useEffect(() => {
    // need related transfer instructions for display
    if (accountId && !isLoadingTransferInstructions && !transferInstructions) {
      dispatcher.cashiering.transferInstructions.all.request(accountId);
    }
  }, [accountId, transferInstructions, isLoadingTransferInstructions, dispatcher.cashiering]);

  const scheduledTransfersData = useSelector(scheduledTransfers);
  const isFetchingScheduledTransfers = useSelector(cashieringSelectors.isFetchingScheduledTransfers);

  useEffect(() => {
    if (accountId && !isFetchingScheduledTransfers && !hasFetchedScheduledTransfers) {
      dispatcher.cashiering.scheduledTransfers.all.request(accountId);
      setHasFetchedScheduledTransfers(true);
    }
  }, [accountId, isFetchingScheduledTransfers, hasFetchedScheduledTransfers, dispatcher.cashiering]);

  const onDelete = useCallback(() => {
    if (transferIdToDelete) {
      dispatcher.cashiering.scheduledTransfers.cancel.request(accountId, transferIdToDelete);
      setTransferIdToDelete(undefined);
    }
  }, [accountId, transferIdToDelete, dispatcher.cashiering]);

  const columns = useMemo(
    () => [
      {
        Header: 'Transaction',
        id: 'transaction',
        width: 1,
        Cell: ({ row }: Cell<Transfer>) => (
          <>
            {row.original.disbursementType &&
            (row.original.disbursementType === 'FullBalance' ||
              row.original.disbursementType === 'FullBalanceAndCloseAccount')
              ? 'FULL BALANCE'
              : formatNumber(
                  row.original.amount
                    ? row.original.amount
                    : row.original.requestedAmount
                    ? row.original.requestedAmount
                    : 0,
                  { currency: true }
                )}{' '}
            <span className="caps">
              {row.original.direction === 'Outgoing'
                ? isIRA
                  ? 'Distribution'
                  : 'Withdrawal'
                : isIRA
                ? 'Contribution'
                : 'Deposit'}
            </span>
            <br />
            <CashieringEntityStatus status={row.original.state}>
              {getTransferStatusText(row.original)}
            </CashieringEntityStatus>
          </>
        ),
      },
      {
        Header: 'Notes',
        id: 'info',
        width: 1,
        Cell: ({ row }: Cell<Transfer>) => {
          // limit note length to 50 characters
          if (row.original.note && row.original.note.length > 50) {
            return (
              <span className="mute" title={row.original.note}>
                {row.original.note.substring(0, 50)}...
              </span>
            );
          } else if (row.original.note) {
            return <span className="mute">{row.original.note}</span>;
          } else {
            return '';
          }
        },
      },
      {
        Header: 'Withholding',
        id: 'withholding',
        width: 1,
        hide: !isIRA,
        Cell: ({ row }: Cell<Transfer>) => {
          const { iraDistribution } = row.original;
          const { stateTaxWithholding, federalTaxWithholding } = iraDistribution || {};

          if (!iraDistribution) return null;

          // need to display the withholding amount for the transfer both state and federal values
          // possibly a percentage or a fixed amount
          // if the withholding amount is a percentage, it should be displayed as a percentage with the dollar amount as well
          // if the withholding amount is a fixed amount, it should be displayed as a dollar amount

          // if the withholding amount is a percentage, it should be displayed as a percentage with the dollar amount as well
          // if the withholding amount is a fixed amount, it should be displayed as a dollar amount
          const stateWithholdingValue = stateTaxWithholding && stateTaxWithholding.value;
          const federalWithholdingValue = federalTaxWithholding && federalTaxWithholding.value;
          const withholdingType =
            (stateTaxWithholding && stateTaxWithholding.valueType) ||
            (federalTaxWithholding && federalTaxWithholding.valueType);

          // convert amount to number
          const amount =
            typeof row.original.requestedAmount === 'string'
              ? parseFloat(row.original.requestedAmount)
              : row.original.requestedAmount;

          if (withholdingType === 'Percentage') {
            const stateWithholding = stateWithholdingValue && amount ? stateWithholdingValue * amount : 0;
            const federalWithholding = federalWithholdingValue && amount ? federalWithholdingValue * amount : 0;
            return (
              <span className="mute">
                State: {formatNumber(stateWithholding, { currency: true })} (
                {(stateWithholdingValue ? stateWithholdingValue * 100 : 0).toFixed(2)}%)
                <br />
                Federal: {formatNumber(federalWithholding, { currency: true })} (
                {(federalWithholdingValue ? federalWithholdingValue * 100 : 0).toFixed(2)}%)
              </span>
            );
          } else {
            return (
              <span className="mute">
                State: {formatNumber(stateWithholdingValue, { currency: true })} (
                {stateWithholdingValue && amount ? ((stateWithholdingValue / amount) * 100).toFixed(2) : '0.00'}%)
                <br />
                Federal: {formatNumber(federalWithholdingValue, { currency: true })} (
                {federalWithholdingValue && amount ? ((federalWithholdingValue / amount) * 100).toFixed(2) : '0.00'}%)
              </span>
            );
          }
        },
      },
      {
        Header: 'Method',
        id: 'method',
        width: 1,
        Cell: ({ row }: Cell<Transfer>) => getTransferMethod(row.original, { achRelationships }),
      },
      {
        Header: 'Transaction Date',
        id: 'createDate',
        canSort: true,
        sortType: onDateSort,
        sortDescFirst: true,
        sortInverted: true,
        width: 1,
        Cell: ({ row }: Cell<Transfer>) => dayjs(row.original.createDate).format('MM/DD/YY h:mm A'),
      },
      {
        Header: ' ',
        id: 'actions',
        width: 1,
        Cell: ({ row }: Cell<Transfer>) => {
          if (row.original.state === 'PendingRepCreateApproval') {
            return (
              <button
                className="btn btn-blend"
                type="button"
                title="Cancel"
                onClick={() => setTransferIdToDelete(row.original.id)}
              >
                <i className="fas fa-2x fa-times-circle" />
              </button>
            );
          }
          return '';
        },
      },
    ],
    [achRelationships]
  );

  const data = useMemo(() => {
    return _.map(_.orderBy(transfers, t => t.createDate, 'desc'), t => ({
      ...t,
      display: t.state === 'Canceled' ? { trClass: 'mute' } : undefined,
    }));
  }, [transfers]);

  const widths = useTableColumnWidths(columns, data);

  const [view, setView] = useState<'transfers' | 'scheduled'>('transfers');

  const mapScheduledTransferStateToEntityState = (state: ScheduledTransferState): TransferState => {
    switch (state) {
      case ScheduledTransferState.Active:
        return 'Active';
      case ScheduledTransferState.Cancelled:
        return 'Canceled';
      case ScheduledTransferState.Completed:
        return 'Complete';
      case ScheduledTransferState.Failed:
        return 'Rejected';
      default:
        return 'Pending';
    }
  };

  const [historyModal, setHistoryModal] = useState<{ open: boolean; history: any[]; nickname?: string }>({
    open: false,
    history: [],
    nickname: '',
  });

  const scheduledColumns = useMemo(
    () => [
      {
        Header: 'Transaction',
        id: 'transaction',
        width: 1,
        Cell: ({ row }: Cell<ScheduledTransfer>) => (
          <div>
            {formatNumber(row.original.amount, { currency: true })}{' '}
            <span className="caps">
              {row.original.direction === 'Outgoing'
                ? isIRA
                  ? 'Distribution'
                  : 'Withdrawal'
                : isIRA
                ? 'Contribution'
                : 'Deposit'}
            </span>
            <br />
            <CashieringEntityStatus status={mapScheduledTransferStateToEntityState(row.original.state)}>
              {row.original.state}
            </CashieringEntityStatus>
          </div>
        ),
      },
      {
        Header: 'Nickname',
        id: 'nickname',
        width: 1,
        Cell: ({ row }: Cell<ScheduledTransfer>) => (
          <div>
            {row.original.nickName || '-'}
            {row.original.iraContribution && (
              <>
                <br />
                <span className="mute">
                  {row.original.iraContribution.type}
                  {row.original.iraContribution.applyToPriorYear ? ' (Prior Year)' : ''}
                </span>
              </>
            )}
            {row.original.iraDistribution && (
              <>
                <br />
                <span className="mute">{row.original.iraDistribution.reason}</span>
              </>
            )}
          </div>
        ),
      },
      {
        Header: 'Schedule',
        id: 'schedule',
        width: 1,
        Cell: ({ row }: Cell<ScheduledTransfer>) => {
          const { frequencyInterval, frequencyUnit, occurrences, remaining, startDate, history } = row.original;
          // Count completed transfers (those with a transferId)
          const completedTransfers = history ? history.filter(h => h.transferId).length : 0;
          // Calculate remaining based on completed transfers
          const actualRemaining = occurrences - completedTransfers;
          // Pluralize the frequency unit
          const pluralUnit = frequencyUnit ? frequencyUnit.toLowerCase() + (frequencyInterval > 1 ? 's' : '') : '';
          return (
            <div>
              Every {frequencyInterval} {pluralUnit}
              {occurrences && occurrences > 0 && (
                <>
                  {' '}
                  ({actualRemaining} of {occurrences} remaining)
                </>
              )}
              <br />
              <span className="mute">
                Next: {dayjs(history && history.length > 0 ? history[0].nextDate : startDate).format('MM/DD/YY')}
              </span>
            </div>
          );
        },
      },
      {
        Header: 'Method',
        id: 'method',
        width: 1,
        Cell: ({ row }: Cell<ScheduledTransfer>) => {
          return <div>{getTransferMethod(row.original, { achRelationships, transferInstructions })}</div>;
        },
      },
      {
        Header: 'Notes',
        id: 'notes',
        width: 1,
        Cell: ({ row }: Cell<ScheduledTransfer>) => {
          if (row.original.memos && row.original.memos.length > 0) {
            const note = row.original.memos.join(', ');
            return note.length > 50 ? (
              <span className="mute" title={note}>
                {note.substring(0, 50)}...
              </span>
            ) : (
              <span className="mute">{note}</span>
            );
          }
          return '';
        },
      },
      {
        Header: 'Created',
        id: 'createDate',
        canSort: true,
        sortType: onDateSort,
        sortDescFirst: true,
        sortInverted: true,
        width: 1,
        Cell: ({ row }: Cell<ScheduledTransfer>) => dayjs(row.original.createDate).format('MM/DD/YY h:mm A'),
      },
      {
        Header: 'History',
        id: 'history',
        width: 1,
        Cell: ({ row }: Cell<ScheduledTransfer>) => (
          <button
            className="btn btn-link"
            type="button"
            title="View History"
            onClick={() =>
              setHistoryModal({
                open: true,
                history: row.original.history || [],
                nickname: row.original.nickName || '',
              })
            }
            style={{ padding: 0 }}
          >
            <i className="fas fa-history" /> View
          </button>
        ),
      },
      {
        Header: 'Actions',
        id: 'actions',
        width: 1,
        className: 'text-center',
        Cell: ({ row }: Cell<ScheduledTransfer>) => {
          if (row.original.state === ScheduledTransferState.Active) {
            return (
              <ul className="inline-actions" style={{ justifyContent: 'center' }}>
                <li>
                  <button
                    className="btn btn-blend"
                    type="button"
                    title="Edit"
                    onClick={() => setEditModal({ open: true, transfer: row.original })}
                  >
                    <i className="fas fa-edit" />
                  </button>
                </li>
                <li>
                  <button
                    className="btn btn-blend"
                    type="button"
                    title="Cancel"
                    onClick={() => setTransferIdToDelete(row.original.id)}
                  >
                    <i className="fas fa-times-circle" />
                  </button>
                </li>
              </ul>
            );
          }
          return '';
        },
      },
    ],
    [isIRA, achRelationships, transferInstructions]
  );
  const scheduledData = useMemo(() => {
    if (!scheduledTransfersData || !Array.isArray(scheduledTransfersData)) return [];
    // Filter out null, undefined, and non-object values before sorting
    const validTransfers = scheduledTransfersData.filter(t => t && typeof t === 'object' && t.createDate);
    return _.map(_.orderBy(validTransfers, t => t.createDate, 'desc'), t => ({
      ...t,
      display: t.state === ScheduledTransferState.Cancelled ? { trClass: 'mute' } : undefined,
    }));
  }, [scheduledTransfersData]);

  const scheduledWidths = useTableColumnWidths(scheduledColumns, scheduledData);

  useEffect(() => {
    if (editModal.open && editModal.transfer) {
      let currentAccount = undefined;
      if (editModal.transfer.mechanism === 'Ach' && achRelationships) {
        currentAccount = achRelationships.find(rel => {
          // if there's no transfer, we can't match anything
          if (!editModal.transfer) return false;
          return rel.id === editModal.transfer.achRelationship;
        });
      } else if (
        (editModal.transfer.mechanism === 'Wire' || editModal.transfer.mechanism === 'Check') &&
        transferInstructions
      ) {
        currentAccount = transferInstructions.find(instr => {
          // if there's no transfer, we can't match anything
          if (!editModal.transfer) return false;
          return instr.id === editModal.transfer.instructionId;
        });
      }

      setEditNickname(editModal.transfer.nickName || '');
      setEditFormValues({
        account: currentAccount,
        transfer: {
          mechanism: editModal.transfer.mechanism,
          ...(editModal.transfer.mechanism === 'Ach' && { achRelationship: editModal.transfer.achRelationship }),
          ...((editModal.transfer.mechanism === 'Wire' || editModal.transfer.mechanism === 'Check') && {
            instructionId: editModal.transfer.instructionId,
          }),
        },
      });
    }
  }, [editModal, achRelationships, transferInstructions]);

  const handleUpdateScheduledTransfer = (values: any) => {
    if (!editModal.transfer) return;

    // Validate required data
    if (!values.transfer || !values.transfer.mechanism) {
      console.error('Transfer mechanism is required');
      return;
    }

    const scheduleId = editModal.transfer.id;
    const mechanism = values.transfer.mechanism;

    let updateData: any = {
      nickName: editNickname,
    };

    // Handle ACH case
    if (mechanism === 'Ach') {
      if (!values.account || !values.account.id) {
        console.error('ACH relationship is required');
        return;
      }
      updateData.relationshipId = values.account.id;
    }
    // Handle Wire/Check case
    else if (mechanism === 'Wire' || mechanism === 'Check') {
      // Check for instructionId in either location
      const instructionId = values.transfer.instructionId || (values.account && values.account.id);
      if (!instructionId) {
        console.error('Transfer instruction is required');
        return;
      }
      updateData.instructionId = instructionId;
    }

    dispatcher.cashiering.scheduledTransfers.update.request(accountId, scheduleId, updateData);

    // Close the modal
    setEditModal({ open: false });
  };

  useEffect(() => {
    if (accountId) {
      dispatcher.cashiering.profile.request(accountId);
    }
  }, [accountId, dispatcher.cashiering]);

  return (
    <>
      {accountCanAccessCashiering && !targetedAccountStatuses && (
        <>
          <div className="container-fluid">
            <div className="nav-tabs-pill">
              <Tabs
                defaultActiveKey={view}
                id="notification-tabs"
                onSelect={(ev: string) => setView(ev as 'transfers' | 'scheduled')}
              >
                <Tab
                  eventKey="transfers"
                  title={
                    <span>
                      <i className="fas fa-history" style={{ marginRight: 4 }} /> Transfer History
                    </span>
                  }
                >
                  <p>
                    <Link
                      className="btn btn-outline-light"
                      to={`/${CashieringRoute.Withdraw}#${CashieringView.Authorize}`}
                    >
                      <i className="fas fa-inbox-out" /> Withdraw funds
                    </Link>{' '}
                    <Link
                      className="btn btn-outline-light"
                      to={`/${CashieringRoute.Deposit}#${CashieringView.Authorize}`}
                    >
                      <i className="fas fa-inbox-in" /> Fund account
                    </Link>{' '}
                    <Link
                      className="btn btn-outline-light"
                      to={`/${CashieringRoute.Accounts}#${CashieringView.Authorize}`}
                    >
                      <i className="fas fa-university" /> Manage accounts
                    </Link>{' '}
                  </p>
                </Tab>
                <Tab
                  eventKey="scheduled"
                  title={
                    <span>
                      <i className="fas fa-clock" style={{ marginRight: 4 }} /> Scheduled Transfers
                    </span>
                  }
                >
                  <p>
                    <Link
                      className="btn btn-outline-light"
                      to={
                        /* e.g. "/scheduled-transfer/out#ScheduledTransferType" */
                        `${generatePath('/scheduled-transfer/:direction', {
                          direction: 'withdrawal',
                        })}#${CashieringView.ScheduledTransferType}`
                      }
                    >
                      <i className="fas fa-inbox-out" /> Schedule {isIRA ? 'Distribution' : 'Withdrawal'}
                    </Link>{' '}
                    <Link
                      className="btn btn-outline-light"
                      to={
                        /* e.g. "/scheduled-transfer/in#ScheduledTransferType" */
                        `${generatePath('/scheduled-transfer/:direction', {
                          direction: 'deposit',
                        })}#${CashieringView.ScheduledTransferType}`
                      }
                    >
                      <i className="fas fa-inbox-in" /> Schedule {isIRA ? 'Contribution' : 'Deposit'}
                    </Link>{' '}
                    <Link
                      className="btn btn-outline-light"
                      to={`/${CashieringRoute.Accounts}#${CashieringView.Authorize}`}
                    >
                      <i className="fas fa-university" /> Manage accounts
                    </Link>{' '}
                  </p>
                </Tab>
              </Tabs>
            </div>
          </div>
        </>
      )}
      {transfers && transfers.length !== 0 && targetedAccountStatuses && (
        <p className="mute">
          {rejectMsg} <ContactPhone />
        </p>
      )}
      {(!transfers || isLoadingTransfers) && view === 'transfers' && <Loading size="small" />}
      {view === 'transfers' && transfers && transfers.length !== 0 ? (
        <BlockTable
          tableKey="transfers"
          sortable
          columnWidths={widths}
          frozenHeaders
          numColumns={_.size(columns)}
          columns={columns}
          data={data}
        />
      ) : targetedAccountStatuses ? (
        <EmptyBlockContent>
          No transfers for current timeframe.
          <br />
          {rejectMsg} <ContactPhone />
        </EmptyBlockContent>
      ) : view === 'transfers' ? (
        <EmptyBlockContent>No transfers for current timeframe.</EmptyBlockContent>
      ) : null}

      {view === 'scheduled' && (
        <>
          {isFetchingScheduledTransfers && <Loading size="small" />}
          {scheduledTransfersData && Array.isArray(scheduledTransfersData) && scheduledTransfersData.length > 0 ? (
            <BlockTable
              tableKey="scheduled-transfers"
              sortable
              columnWidths={scheduledWidths}
              frozenHeaders
              numColumns={_.size(scheduledColumns)}
              columns={scheduledColumns}
              data={scheduledData}
            />
          ) : (
            <EmptyBlockContent>No scheduled transfers found.</EmptyBlockContent>
          )}
        </>
      )}

      {transferIdToDelete && (
        <Modal show={true} setShow={() => true} backdrop="static">
          <BootstrapModal.Header>
            <BootstrapModal.Title>Confirm</BootstrapModal.Title>
          </BootstrapModal.Header>
          <BootstrapModal.Body>
            <p>Are you sure you want to cancel this {view === 'scheduled' ? 'scheduled transfer' : 'transfer'}?</p>
          </BootstrapModal.Body>
          <BootstrapModal.Footer className="modal-footer-justified">
            <Button variant="secondary" onClick={() => setTransferIdToDelete(undefined)}>
              Go back
            </Button>
            <Button variant="danger" onClick={onDelete}>
              Cancel {view === 'scheduled' ? 'scheduled transfer' : 'transfer'}
            </Button>
          </BootstrapModal.Footer>
        </Modal>
      )}

      <Modal
        show={historyModal.open}
        setShow={() => setHistoryModal({ ...historyModal, open: false })}
        backdrop="static"
      >
        <BootstrapModal.Header>
          <BootstrapModal.Title>
            History{historyModal.nickname ? `: ${historyModal.nickname}` : ''}
          </BootstrapModal.Title>
        </BootstrapModal.Header>
        <BootstrapModal.Body>
          {historyModal.history && historyModal.history.length > 0 ? (
            <table className="table table-sm table-striped">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {historyModal.history.map((h, idx) => (
                  <tr key={idx}>
                    <td>{dayjs(h.scheduledDate).format('MM/DD/YY')}</td>
                    <td>{h.state}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <span className="mute">No history available.</span>
          )}
        </BootstrapModal.Body>
        <BootstrapModal.Footer>
          <Button variant="secondary" onClick={() => setHistoryModal({ ...historyModal, open: false })}>
            Close
          </Button>
        </BootstrapModal.Footer>
      </Modal>

      <Modal
        show={editModal.open}
        setShow={open => setEditModal({ open, transfer: open ? editModal.transfer : undefined })}
        backdrop="static"
      >
        <Formik
          initialValues={editFormValues}
          enableReinitialize
          onSubmit={() => {
            /* noop — we submit manually */
          }}
        >
          {({ values, setValues }) => (
            <>
              <BootstrapModal.Header closeButton>
                <BootstrapModal.Title>Edit Scheduled Transfer</BootstrapModal.Title>
              </BootstrapModal.Header>

              <BootstrapModal.Body>
                <div className="form-group">
                  <label>Nickname</label>
                  <input
                    className="form-control"
                    type="text"
                    value={editNickname}
                    onChange={e => setEditNickname(e.target.value)}
                  />
                </div>

                <div style={{ marginTop: 20 }}>
                  <useCashieringDataContext.Provider>
                    <TransferAccountsList
                      values={values}
                      setValues={setValues}
                      route={
                        editModal.transfer && editModal.transfer.direction === 'Outgoing'
                          ? CashieringRoute.Withdraw
                          : CashieringRoute.Deposit
                      }
                      lockedMechanism={editModal.transfer && editModal.transfer.mechanism}
                    />
                  </useCashieringDataContext.Provider>
                </div>
              </BootstrapModal.Body>

              <BootstrapModal.Footer>
                <Button variant="secondary" onClick={() => setEditModal({ open: false })}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    const errors = onValidate(values);
                    if (!Object.keys(errors).length) {
                      // manual submit with the up-to-date Formik values
                      handleUpdateScheduledTransfer(values);
                      setEditModal({ open: false });
                    }
                  }}
                >
                  Save
                </Button>
              </BootstrapModal.Footer>
            </>
          )}
        </Formik>
      </Modal>
    </>
  );
};
